import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Button, Row, Col, Form, Input, FormFeedback } from "reactstrap";
import { useFormik } from "formik";
import DatePicker from "react-tailwindcss-datepicker";
import Avatar from "components/Avatar/Avatar";
import { simpleObjectToFormData } from "utils/formData";
import { AdminValidationSchema } from "model/_base/admin/admin.schema";

/**
 * InfoForm - A reusable form component for user information
 * This component only handles form state and validation.
 * All submission logic is handled by the parent component.
 *
 * @param {Object} data - Initial form data
 * @param {Function} onSwitchView - Callback when switching between view/edit modes
 * @param {boolean} isEditMode - Whether the form is in edit mode
 * @param {Object} formRef - Ref to access form methods from parent
 * @param {Function} onSubmit - Callback when form is submitted with values
 * @param {boolean} isSubmitting - Whether the form is in submitting state
 * @param {Object} submitError - Error object from submission
 */
const InfoForm = ({ data, onSwitchView, isEditMode = true, formRef, onSubmit, isSubmitting = false }) => {
  const [profilePicture, setProfilePicture] = useState(data?.profilePicture);
  const [selectedImage, setSelectedImage] = useState(null);

  // Expose validation methods to parent
  React.useImperativeHandle(formRef, () => ({
    submitForm: validation.submitForm,
    values: validation.values,
    isValid: validation.isValid,
    errors: validation.errors,
    touched: validation.touched,
    setFieldValue: validation.setFieldValue,
    setFieldTouched: validation.setFieldTouched,
    validateForm: validation.validateForm,
  }));

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: data?.email,
      firstName: data?.firstName,
      lastName: data?.lastName,
      birthdate: data?.birthdate,
      phoneNo: data?.phoneNo,
    },
    validationSchema: AdminValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (isEditMode && onSubmit) {
        try {
          await onSubmit(selectedImage ? simpleObjectToFormData(values) : values);
        } finally {
          setSubmitting(false);
        }
      }
    },
  });

  // Handle image selection
  const handleImageChange = (file) => {
    if (file) {
      // Create a preview URL for the selected image
      const imageUrl = URL.createObjectURL(file);
      setProfilePicture(imageUrl);
      setSelectedImage(file);

      // You can also add the file to form values if needed
      validation.setFieldValue("file", file);
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (profilePicture && profilePicture.startsWith("blob:")) {
        URL.revokeObjectURL(profilePicture);
      }
    };
  }, [profilePicture]);

  const handleDateChange = (value) => {
    validation.setFieldValue("birthdate", value.startDate);
  };

  const handleCancel = () => {
    if (isEditMode && onSwitchView) {
      onSwitchView();
    }
  };

  return (
    <Form onSubmit={validation.handleSubmit} id={!isEditMode ? "info-form" : undefined}>
      <div className="flex mt-2 gap-4">
        <div className="flex-shrink-0">
          <Avatar size="lg" src={profilePicture} alt={`${data?.firstName} ${data?.lastName}`} editable={true} onImageChange={handleImageChange} />
        </div>
        <Row className="flex-1">
          <Col xs={6}>
            <div className="text-2xl whitespace-nowrap">ชื่อ</div>
            <Input name="firstName" type="text" placeholder="ชื่อ" onChange={validation.handleChange} onBlur={validation.handleBlur} value={validation.values.firstName} invalid={validation.touched.firstName && validation.errors.firstName} data-testid="firstname-input" />
            {validation.touched.firstName && validation.errors.firstName && <FormFeedback>{validation.errors.firstName}</FormFeedback>}
          </Col>
          <Col xs={6}>
            <div className="text-2xl whitespace-nowrap">นามสกุล</div>
            <Input name="lastName" type="text" placeholder="นามสกุล" onChange={validation.handleChange} onBlur={validation.handleBlur} value={validation.values.lastName} invalid={validation.touched.lastName && validation.errors.lastName} data-testid="lastname-input" />
            {validation.touched.lastName && validation.errors.lastName && <FormFeedback>{validation.errors.lastName}</FormFeedback>}
          </Col>
          <Col md={12} className="mt-4">
            <div className="text-2xl whitespace-nowrap">วันเกิด</div>
            <div className="datepicker">
              <DatePicker
                useRange={false}
                asSingle={true}
                value={{
                  startDate: validation.values.birthdate || new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
                  endDate: validation.values.birthdate || new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
                }}
                onChange={handleDateChange}
                displayFormat="DD/MM/YYYY"
                placeholder="เลือกวันเกิด"
                inputClassName={`form-control ${validation.touched.birthdate && validation.errors.birthdate ? "is-invalid" : ""}`}
                maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                startFrom={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
              />
              {validation.touched.birthdate && validation.errors.birthdate && <div className="invalid-feedback d-block">{validation.errors.birthdate}</div>}
            </div>
          </Col>
          <Col md={12} className="mt-4">
            <div className="text-2xl whitespace-nowrap">เบอร์โทรศัพท์</div>
            <Input
              type="tel"
              id="phoneNo"
              name="phoneNo"
              placeholder="กรอกเบอร์โทรศัพท์"
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/\D/g, "");
                validation.setFieldValue("phoneNo", value);
              }}
              onBlur={validation.handleBlur}
              value={validation.values.phoneNo}
              invalid={validation.touched.phoneNo && validation.errors.phoneNo}
            />
            {validation.touched.phoneNo && validation.errors.phoneNo && <FormFeedback>{validation.errors.phoneNo}</FormFeedback>}
          </Col>
        </Row>
      </div>

      {isEditMode && (
        <div className="text-end mt-4">
          <div className="d-flex justify-content-between gap-3 w-100">
            <Button color="light" outline className="w-52" onClick={handleCancel}>
              ยกเลิก
            </Button>
            <Button className="w-52" color="primary" type="submit" disabled={validation.isSubmitting || isSubmitting}>
              {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default withTranslation()(InfoForm);
